import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import * as LucideIcons from 'lucide-react';
import {
  Link, Plus, Trash2, Pencil, ChevronUp, ChevronDown,
  Loader2, ExternalLink, Copy, Check, Globe, Search, SquarePen, RefreshCw, RotateCcw,
} from 'lucide-react';
import { API } from '@/lib/helpers';

const DEFAULT_PAGE_STYLE = {
  icone_pagina: 'Home',
  cor_icone: '#ffffff',
  cor_fundo_icone: '#2663eb',
  cor_fundo: '#f8fafc',
  cor_fundo_2: '#e5f6ff',
  cor_fonte_nome: '#333333',
  cor_fonte_bio: '#333333',
  fonte: 'Inter',
  bg_type: 'gradient',
  bg_gradient_direction: 'to bottom right',
  bg_preset: '',
};

const DEFAULT_LINK_STYLE = {
  cor_botao: '#ffffff',
  cor_borda: '#ffffff',
  cor_fonte: '#333333',
  tamanho_borda: 2,
  tipo_borda: 'solid',
  border_radius: 15,
};

// Build icon list dynamically from all lucide-react exports
const ALL_ICONS = Object.keys(LucideIcons)
  .filter(name =>
    /^[A-Z]/.test(name) &&
    !name.endsWith('Icon') &&
    !name.startsWith('Lucide') &&
    !name.startsWith('create') &&
    name !== 'default' &&
    name !== 'icons' &&
    name !== 'Church' &&
    LucideIcons[name]?.render
  )
  .sort();

const FONT_OPTIONS = [
  { value: 'Inter', label: 'Inter (Moderna)' },
  { value: 'Poppins', label: 'Poppins (Amigável)' },
  { value: 'Georgia', label: 'Georgia (Clássica)' },
  { value: 'Playfair Display', label: 'Playfair (Elegante)' },
  { value: 'Roboto Mono', label: 'Roboto Mono (Técnica)' },
];

const BORDER_TYPES = [
  { value: 'solid', label: 'Sólida' },
  { value: 'dashed', label: 'Tracejada' },
  { value: 'dotted', label: 'Pontilhada' },
];

const BG_PRESETS = [
  { id: 'lavender_mist', label: 'Lavanda', css: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 50%, #f0e7fe 100%)' },
  { id: 'rose_quartz', label: 'Rose', css: 'linear-gradient(135deg, #fff1f2 0%, #fce7f3 50%, #fdf2f8 100%)' },
  { id: 'morning_sky', label: 'Céu', css: 'linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)' },
  { id: 'sage_garden', label: 'Sálvia', css: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #f0fdf9 100%)' },
  { id: 'warm_sand', label: 'Areia', css: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 40%, #fff7ed 100%)' },
  { id: 'pearl', label: 'Pérola', css: 'linear-gradient(180deg, #fafafa 0%, #f5f5f4 50%, #fafaf9 100%)' },
  { id: 'soft_lilac', label: 'Lilás', css: 'linear-gradient(180deg, #faf5ff 0%, #f3e8ff 60%, #faf5ff 100%)' },
  { id: 'ocean_breeze', label: 'Oceano', css: 'linear-gradient(135deg, #ecfeff 0%, #e0f2fe 50%, #eff6ff 100%)' },
  { id: 'blush_peach', label: 'Pêssego', css: 'linear-gradient(135deg, #fff7ed 0%, #fff1f2 50%, #fef2f2 100%)' },
  { id: 'mint_cream', label: 'Menta', css: 'linear-gradient(180deg, #f0fdfa 0%, #ecfdf5 50%, #f0fdf4 100%)' },
  { id: 'twilight', label: 'Crepúsculo', css: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #3730a3 100%)' },
  { id: 'midnight', label: 'Noite', css: 'linear-gradient(180deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)' },
];

const GRADIENT_DIRECTIONS = [
  { value: 'to bottom', label: 'Cima → Baixo' },
  { value: 'to right', label: 'Esquerda → Direita' },
  { value: 'to bottom right', label: 'Diagonal ↘' },
  { value: 'to bottom left', label: 'Diagonal ↙' },
];

const EMPTY_LINK = {
  titulo: '', descricao: '', url: '', icone: 'Link',
  ...DEFAULT_LINK_STYLE,
  ativo: true,
};

function IconPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const SelectedIcon = LucideIcons[value] || Link;

  const filtered = useMemo(() => {
    if (!search.trim()) return ALL_ICONS;
    const q = search.toLowerCase();
    return ALL_ICONS.filter(name => name.toLowerCase().includes(q));
  }, [search]);

  return (
    <>
      <button type="button" onClick={() => { setOpen(true); setSearch(''); }}
        className="w-full flex items-center gap-2 px-3 py-2 border rounded-lg bg-white hover:bg-slate-50 transition-colors"
        data-testid="icon-picker-trigger">
        <SelectedIcon className="w-5 h-5 text-slate-700" />
        <span className="text-sm text-slate-600 flex-1 text-left">{value}</span>
        <Pencil className="w-3 h-3 text-slate-400" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Selecionar Ícone</DialogTitle><DialogDescription>Pesquise entre {ALL_ICONS.length} ícones disponíveis</DialogDescription></DialogHeader>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar ícone..." className="pl-9" data-testid="icon-search" autoFocus />
          </div>
          <div className="grid grid-cols-8 gap-1.5 max-h-64 overflow-y-auto p-1">
            {filtered.map(name => {
              const Icon = LucideIcons[name];
              if (!Icon) return null;
              return (
                <button key={name} onClick={() => { onChange(name); setOpen(false); }}
                  className={`p-2 rounded-lg flex items-center justify-center transition-colors ${value === name ? 'bg-blue-100 ring-2 ring-blue-500' : 'hover:bg-slate-100'}`}
                  title={name}>
                  <Icon className="w-4.5 h-4.5" />
                </button>
              );
            })}
          </div>
          {filtered.length === 0 && <p className="text-center text-sm text-slate-400 py-4">Nenhum ícone encontrado</p>}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function LinksSettingsTab({ refreshSettings: parentRefreshSettings, openSlugEditor }) {
  const [links, setLinks] = useState([]);
  const [pageSettings, setPageSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_LINK);
  const [editId, setEditId] = useState(null);
  const [editSystemType, setEditSystemType] = useState(null);
  const [pageDialog, setPageDialog] = useState(false);
  const [copied, setCopied] = useState(false);
  const [churchSlug, setChurchSlug] = useState('');
  const [slugCadastro, setSlugCadastro] = useState('');
  const [slugVisitas, setSlugVisitas] = useState('');
  const [slugAtualizar, setSlugAtualizar] = useState('');
  const [addons, setAddons] = useState({});
  const [termsOfUse, setTermsOfUse] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [resetStyleConfirm, setResetStyleConfirm] = useState(false);
  const [slugEditing, setSlugEditing] = useState(false);
  const [slugForm, setSlugForm] = useState('');
  const [showLinksInfo, setShowLinksInfo] = useState(false);

  const headers = { 'Content-Type': 'application/json' };

  const fetchLinks = useCallback(async () => {
    try {
      const [linksRes, settingsRes] = await Promise.all([
        fetch(`${API}/links`, { credentials: 'include' }),
        fetch(`${API}/link-page-settings`, { credentials: 'include' }),
      ]);
      if (linksRes.ok) setLinks(await linksRes.json());
      if (settingsRes.ok) setPageSettings(await settingsRes.json());
    } catch {
      // Mantém a tela funcional mesmo se uma leitura pontual falhar.
    } finally { setLoading(false); }
  }, []);

  // Fetch church settings (slug, addons, terms)
  useEffect(() => {
    fetchLinks();
    fetch(`${API}/settings`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.slug_linktree) setChurchSlug(d.slug_linktree);
        else if (d?.slug_cadastro) setChurchSlug(d.slug_cadastro);
        if (d?.slug_cadastro) setSlugCadastro(d.slug_cadastro);
        if (d?.slug_visitas) setSlugVisitas(d.slug_visitas);
        if (d?.slug_atualizar) setSlugAtualizar(d.slug_atualizar);
        if (d?.terms_of_use) setTermsOfUse(d.terms_of_use);
        if (d?.addons) setAddons(d.addons);
      })
      .catch(() => {});
  }, [fetchLinks]);

  // Keep system public links visible so admins can copy/configure URLs even on new churches.
  const visibleLinks = useMemo(() => {
    return links;
  }, [links]);

  // Auto-create system cadastro link if addon is enabled but no system link exists
  useEffect(() => {
    if (loading || !slugCadastro) return;
    const hasSystemLink = links.some(l => l.system_type === 'cadastro');
    if (!hasSystemLink) {
      const payload = {
        titulo: 'Cadastro',
        descricao: 'Formulário de cadastro público',
        url: `/pessoas/${slugCadastro}`,
        icone: 'UserRoundPlus',
        ...DEFAULT_LINK_STYLE,
        ativo: true, system_type: 'cadastro',
      };
      fetch(`${API}/links`, { method: 'POST', headers, credentials: 'include', body: JSON.stringify(payload) })
        .then(r => { if (r.ok) fetchLinks(); })
        .catch(() => {});
    }
  }, [loading, links, slugCadastro, fetchLinks]);

  // Auto-create system visitas link if addon is enabled but no system link exists
  useEffect(() => {
    if (loading || !slugVisitas || !addons.visitas) return;
    const hasSystemLink = links.some(l => l.system_type === 'visitas');
    if (!hasSystemLink) {
      const payload = {
        titulo: 'Solicitação de Visita',
        descricao: 'Formulário público para solicitação de visitas',
        url: `/visitas/${slugVisitas}`,
        icone: 'Home',
        ...DEFAULT_LINK_STYLE,
        ativo: true, system_type: 'visitas',
      };
      fetch(`${API}/links`, { method: 'POST', headers, credentials: 'include', body: JSON.stringify(payload) })
        .then(r => { if (r.ok) fetchLinks(); })
        .catch(() => {});
    }
  }, [loading, links, slugVisitas, addons.visitas, fetchLinks]);

  // Auto-create system atualizar link if no system link exists
  useEffect(() => {
    if (loading || !slugAtualizar) return;
    const hasSystemLink = links.some(l => l.system_type === 'atualizar');
    if (!hasSystemLink) {
      const payload = {
        titulo: 'Atualizar Dados',
        descricao: 'Atualize seus dados cadastrais',
        url: `/atualizar/${slugAtualizar}`,
        icone: 'UserRoundPen',
        ...DEFAULT_LINK_STYLE,
        ativo: true, system_type: 'atualizar',
      };
      fetch(`${API}/links`, { method: 'POST', headers, credentials: 'include', body: JSON.stringify(payload) })
        .then(r => { if (r.ok) fetchLinks(); })
        .catch(() => {});
    }
  }, [loading, links, slugAtualizar, fetchLinks]);

  const openCreate = () => { setEditForm(EMPTY_LINK); setEditId(null); setEditSystemType(null); setEditDialog(true); };

  const openEdit = (link) => {
    setEditForm({
      titulo: link.titulo, descricao: link.descricao || '', url: link.url || '', icone: link.icone,
      cor_botao: link.cor_botao, cor_borda: link.cor_borda, cor_fonte: link.cor_fonte,
      tamanho_borda: link.tamanho_borda, tipo_borda: link.tipo_borda,
      border_radius: link.border_radius, ativo: link.ativo,
    });
    setEditId(link.link_id);
    setEditSystemType(link.system_type || null);
    setEditDialog(true);
  };

  const handleSave = async () => {
    if (!editForm.titulo.trim()) { toast.error('Título é obrigatório'); return; }
    if (!editSystemType && !editForm.url.trim()) { toast.error('URL é obrigatória'); return; }
    setSaving(true);
    try {
      const payload = { ...editForm };
      // For system cadastro links, auto-set the URL
      if (editSystemType === 'cadastro') {
        payload.url = `/pessoas/${slugCadastro}`;
        payload.system_type = 'cadastro';
      }
      if (editSystemType === 'visitas') {
        payload.url = `/visitas/${slugVisitas}`;
        payload.system_type = 'visitas';
      }
      if (editSystemType === 'atualizar') {
        payload.url = `/atualizar/${slugAtualizar}`;
        payload.system_type = 'atualizar';
      }
      const url = editId ? `${API}/links/${editId}` : `${API}/links`;
      const method = editId ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers, credentials: 'include', body: JSON.stringify(payload) });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Erro ao salvar'); }

      // If editing system cadastro link, also save terms_of_use if changed
      if (editSystemType === 'cadastro') {
        await fetch(`${API}/settings`, {
          method: 'PUT', headers, credentials: 'include',
          body: JSON.stringify({ terms_of_use: termsOfUse }),
        }).catch(() => {});
        if (parentRefreshSettings) parentRefreshSettings();
      }

      toast.success(editId ? 'Link atualizado!' : 'Link criado!');
      setEditDialog(false);
      fetchLinks();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (linkId) => {
    try {
      const r = await fetch(`${API}/links/${linkId}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error();
      toast.success('Link excluído');
      setDeleteConfirm(null);
      fetchLinks();
    } catch { toast.error('Erro ao excluir'); }
  };

  const handleToggle = async (link) => {
    try {
      const r = await fetch(`${API}/links/${link.link_id}/toggle`, { method: 'PUT', credentials: 'include' });
      if (!r.ok) throw new Error();
      // For system cadastro link, also toggle the addon
      if (link.system_type === 'cadastro') {
        const newAtivo = !link.ativo;
        await fetch(`${API}/settings`, {
          method: 'PUT', headers, credentials: 'include',
          body: JSON.stringify({ addons: { cadastro_link: newAtivo } }),
        }).catch(() => {});
        if (parentRefreshSettings) parentRefreshSettings();
      }
      fetchLinks();
    } catch { toast.error('Erro ao alterar status'); }
  };

  const handleDuplicate = async (link) => {
    const payload = {
      titulo: `${link.titulo} (cópia)`,
      descricao: link.descricao || '',
      url: link.url,
      icone: link.icone,
      cor_botao: link.cor_botao,
      cor_borda: link.cor_borda,
      cor_fonte: link.cor_fonte,
      tamanho_borda: link.tamanho_borda,
      tipo_borda: link.tipo_borda,
      border_radius: link.border_radius,
      ativo: link.ativo,
    };
    try {
      const r = await fetch(`${API}/links`, { method: 'POST', headers, credentials: 'include', body: JSON.stringify(payload) });
      if (!r.ok) throw new Error();
      toast.success('Link duplicado!');
      fetchLinks();
    } catch { toast.error('Erro ao duplicar'); }
  };

  const moveLink = async (index, direction) => {
    const newLinks = [...links];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= newLinks.length) return;
    [newLinks[index], newLinks[swapIndex]] = [newLinks[swapIndex], newLinks[index]];
    setLinks(newLinks);
    try {
      await fetch(`${API}/links/reorder/bulk`, {
        method: 'PUT', headers, credentials: 'include',
        body: JSON.stringify({ order: newLinks.map(l => l.link_id) }),
      });
    } catch { toast.error('Erro ao reordenar'); fetchLinks(); }
  };

  const savePageSettings = async () => {
    if (!pageSettings) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/link-page-settings`, {
        method: 'PUT', headers, credentials: 'include',
        body: JSON.stringify(pageSettings),
      });
      if (!r.ok) throw new Error();
      toast.success('Configurações da página salvas!');
      setPageDialog(false);
    } catch { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const resetDefaultStyle = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/link-page-settings/reset-default-style`, {
        method: 'PUT', headers, credentials: 'include',
      });
      if (!r.ok) throw new Error();
      setPageSettings(prev => ({ ...(prev || {}), ...DEFAULT_PAGE_STYLE }));
      toast.success('Aparência padrão restaurada!');
      setResetStyleConfirm(false);
      fetchLinks();
    } catch { toast.error('Erro ao resetar aparência'); }
    finally { setSaving(false); }
  };

  const saveSlug = async () => {
    const cleaned = slugForm.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
    if (!cleaned) { toast.error('URL inválida'); return; }
    try {
      const r = await fetch(`${API}/church/slug`, {
        method: 'PUT', headers, credentials: 'include',
        body: JSON.stringify({ slug: cleaned, type: 'linktree' }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Erro'); }
      setChurchSlug(cleaned);
      setSlugEditing(false);
      toast.success('URL atualizada!');
    } catch (err) { toast.error(err.message); }
  };

  const publicUrl = churchSlug ? `${window.location.origin}/l/${churchSlug}` : '';

  const copyUrl = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="links-settings">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap" style={{marginBottom: '24px'}}>
        <div>
          <h3 className="font-semibold text-slate-800 flex items-center gap-1.5">Links
            <button onClick={() => setShowLinksInfo(true)} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="links-info-btn"><LucideIcons.HelpCircle className="w-4 h-4" /></button>
          </h3>
          <p className="text-xs text-slate-500">Gerencie os links da sua página pública</p>
        </div>
        <div className="flex gap-2">
          {publicUrl && (
            <a href={publicUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="icon" data-testid="btn-open-page" title="Ver Página">
                <ExternalLink className="w-4 h-4" />
              </Button>
            </a>
          )}
          <Button variant="outline" size="icon" onClick={() => setResetStyleConfirm(true)} disabled={saving} data-testid="btn-reset-default-link-style" title="Resetar configurações padrões">
            <RotateCcw className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={() => setPageDialog(true)} data-testid="btn-page-settings" title="Configurações da Página">
            <LucideIcons.Settings className="w-4 h-4" />
          </Button>
          <Button size="sm" onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-add-link">
            <Plus className="w-4 h-4 mr-1.5" />Adicionar
          </Button>
        </div>
      </div>

      {/* Public URL */}
      {publicUrl && (
        <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl border border-slate-200" style={{marginBottom: '24px'}}>
          <Link className="w-4 h-4 text-blue-600 flex-shrink-0" />
          {slugEditing ? (
            <>
              <div className="flex items-center flex-1 min-w-0 gap-1">
                <span className="text-sm text-slate-400 flex-shrink-0">{window.location.origin}/l/</span>
                <Input value={slugForm} onChange={e => setSlugForm(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  className="h-7 text-sm flex-1" data-testid="input-slug" autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') saveSlug(); if (e.key === 'Escape') setSlugEditing(false); }} />
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSlugForm(Math.random().toString(36).substring(2, 10))} title="Gerar aleatório" data-testid="btn-random-slug">
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={saveSlug} className="text-green-600 hover:text-green-700" data-testid="btn-save-slug">
                <Check className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <span className="text-sm text-slate-600 truncate flex-1">{churchSlug}</span>
              <Button variant="ghost" size="sm" onClick={copyUrl} data-testid="btn-copy-url">
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              </Button>
              <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" size="sm" data-testid="btn-open-url"><ExternalLink className="w-4 h-4" /></Button>
              </a>
              <Button variant="ghost" size="sm" onClick={() => { setSlugForm(churchSlug); setSlugEditing(true); }} data-testid="btn-edit-slug" title="Editar URL">
                <SquarePen className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      )}

      {/* Links List */}
      {visibleLinks.length === 0 ? (
        <div className="text-center py-10 text-slate-400">
          <Link className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm">Nenhum link criado ainda</p>
        </div>
      ) : (
        <div>
          {visibleLinks.map((link, idx) => {
            const IconComp = LucideIcons[link.icone] || Link;
            const isSystem = !!link.system_type;
            return (
              <div key={link.link_id}
                className={`flex items-center gap-3 px-4 py-4 rounded-lg border transition-colors ${link.ativo ? 'bg-white border-slate-200 hover:bg-slate-50' : 'bg-slate-50 border-slate-100 opacity-60'}`}
                style={{marginBottom: '6px'}}
                data-testid={`link-item-${link.link_id}`}>
                <div className="flex flex-col gap-0.5">
                  <button onClick={() => moveLink(idx, -1)} disabled={idx === 0}
                    className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                  <button onClick={() => moveLink(idx, 1)} disabled={idx === visibleLinks.length - 1}
                    className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                </div>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: link.cor_botao }}>
                  <IconComp className="w-4 h-4" style={{ color: link.cor_fonte }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-800 truncate">{link.titulo}</p>
                    {isSystem && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 flex-shrink-0">Sistema</span>}
                  </div>
                  <p className="text-xs text-slate-400 truncate">{isSystem ? (link.descricao || 'Link do sistema') : link.url}</p>
                </div>
                {!isSystem && (
                  <Button variant="ghost" size="sm" onClick={() => handleDuplicate(link)} data-testid={`duplicate-${link.link_id}`} title="Duplicar">
                    <Copy className="w-4 h-4" />
                  </Button>
                )}
                {isSystem && openSlugEditor && (
                  <Button variant="ghost" size="sm" onClick={() => openSlugEditor(link.system_type)} title="Editar URL" data-testid={`slug-${link.link_id}`}>
                    <Globe className="w-4 h-4" />
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => openEdit(link)} data-testid={`edit-${link.link_id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                {!isSystem ? (
                  <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(link)} className="text-red-500 hover:text-red-700" data-testid={`delete-${link.link_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                ) : (
                  <Switch checked={link.ativo} onCheckedChange={() => handleToggle(link)} data-testid={`toggle-system-${link.link_id}`} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Edit/Create Link Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? 'Editar Link' : 'Novo Link'}{editSystemType && <span className="ml-2 text-xs font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">Sistema</span>}</DialogTitle>
            <DialogDescription>{editSystemType === 'cadastro' ? 'Configure o link de cadastro público' : 'Preencha as informações do link'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            {/* Geral Group */}
            <div className="border border-slate-200 rounded-xl p-4 space-y-3">
              <Label className="text-sm font-semibold text-slate-700 block">Geral</Label>
              <div><Label>Título *</Label><Input value={editForm.titulo} onChange={e => setEditForm({...editForm, titulo: e.target.value})} placeholder="Ex: Instagram" data-testid="input-link-titulo" /></div>
              {!editSystemType && (
                <div><Label>URL *</Label><Input value={editForm.url} onChange={e => setEditForm({...editForm, url: e.target.value})} placeholder="https://..." data-testid="input-link-url" /></div>
              )}
              <div><Label>Descrição</Label><Input value={editForm.descricao} onChange={e => setEditForm({...editForm, descricao: e.target.value})} placeholder="Breve descrição (opcional)" data-testid="input-link-desc" /></div>
            </div>

            {/* Terms of Use - only for cadastro system link */}
            {editSystemType === 'cadastro' && (
              <div className="border border-slate-200 rounded-xl p-4 space-y-3" style={{marginTop: '20px'}}>
                <Label className="text-sm font-semibold text-slate-700 block">Termo de Uso</Label>
                <textarea value={termsOfUse} onChange={e => setTermsOfUse(e.target.value)}
                  placeholder="Exibido no formulário de cadastro público. Deixe vazio para não exibir."
                  rows={4}
                  className="flex w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  data-testid="input-terms-of-use" />
              </div>
            )}

            {/* Design Group */}
            <div className="border border-slate-200 rounded-xl p-4 space-y-3">
              <Label className="text-sm font-semibold text-slate-700 block">Design</Label>
              <div><Label>Ícone</Label><IconPicker value={editForm.icone} onChange={v => setEditForm({...editForm, icone: v})} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Cor do botão</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={editForm.cor_botao} onChange={e => setEditForm({...editForm, cor_botao: e.target.value})} className="w-8 h-8 rounded cursor-pointer border-0" data-testid="color-botao" />
                    <Input value={editForm.cor_botao} onChange={e => setEditForm({...editForm, cor_botao: e.target.value})} className="text-xs" />
                  </div>
                </div>
                <div>
                  <Label>Cor da fonte</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={editForm.cor_fonte} onChange={e => setEditForm({...editForm, cor_fonte: e.target.value})} className="w-8 h-8 rounded cursor-pointer border-0" data-testid="color-fonte" />
                    <Input value={editForm.cor_fonte} onChange={e => setEditForm({...editForm, cor_fonte: e.target.value})} className="text-xs" />
                  </div>
                </div>
                <div>
                  <Label>Cor da borda</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={editForm.cor_borda} onChange={e => setEditForm({...editForm, cor_borda: e.target.value})} className="w-8 h-8 rounded cursor-pointer border-0" data-testid="color-borda" />
                    <Input value={editForm.cor_borda} onChange={e => setEditForm({...editForm, cor_borda: e.target.value})} className="text-xs" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Arredondamento</Label>
                  <Input type="number" min={0} max={50} value={editForm.border_radius} onChange={e => setEditForm({...editForm, border_radius: parseInt(e.target.value) || 0})} data-testid="input-radius" />
                </div>
                <div>
                  <Label>Tamanho borda</Label>
                  <Input type="number" min={0} max={10} value={editForm.tamanho_borda} onChange={e => setEditForm({...editForm, tamanho_borda: parseInt(e.target.value) || 0})} data-testid="input-border-width" />
                </div>
                <div>
                  <Label>Tipo borda</Label>
                  <Select value={editForm.tipo_borda} onValueChange={v => setEditForm({...editForm, tipo_borda: v})}>
                    <SelectTrigger data-testid="select-border-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {BORDER_TYPES.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Como vai ficar Group */}
            <div className="border border-slate-200 rounded-xl p-4 space-y-3">
              <Label className="text-sm font-semibold text-slate-700 block">Como vai ficar</Label>
              <div className="p-4 bg-slate-50 rounded-xl">
                <div
                  className="flex items-center gap-3 w-full"
                  style={{
                    backgroundColor: editForm.cor_botao, borderColor: editForm.cor_borda,
                    borderWidth: `${editForm.tamanho_borda}px`, borderStyle: editForm.tipo_borda,
                    borderRadius: `${editForm.border_radius}px`, padding: '14px 20px',
                  }}>
                  {(() => { const I = LucideIcons[editForm.icone] || Link; return <I className="w-5 h-5" style={{ color: editForm.cor_fonte }} />; })()}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate" style={{ color: editForm.cor_fonte }}>{editForm.titulo || 'Título do link'}</p>
                    {editForm.descricao && <p className="text-xs truncate mt-0.5 opacity-80" style={{ color: editForm.cor_fonte }}>{editForm.descricao}</p>}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2">
                <Switch checked={editForm.ativo} onCheckedChange={v => setEditForm({...editForm, ativo: v})} data-testid="toggle-edit-ativo" />
                <Label className="text-sm">{editForm.ativo ? 'Ativo' : 'Inativo'}</Label>
              </div>
              <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-save-link">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                {editId ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Page Settings Dialog */}
      <Dialog open={pageDialog} onOpenChange={setPageDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configurações da Página</DialogTitle>
            <DialogDescription>Personalize a aparência da sua página pública de links</DialogDescription>
          </DialogHeader>
          {pageSettings && (
            <div className="space-y-4">
              {/* General Group */}
              <div className="border border-slate-200 rounded-xl p-4 space-y-3">
                <Label className="text-sm font-semibold text-slate-700 block">Geral</Label>
                <div><Label>Título da Página</Label><Input value={pageSettings.nome_igreja || ''} onChange={e => setPageSettings({...pageSettings, nome_igreja: e.target.value})} data-testid="input-page-nome" /></div>
                <div><Label>Descrição</Label><textarea value={pageSettings.bio || ''} onChange={e => setPageSettings({...pageSettings, bio: e.target.value})} placeholder="Uma breve descrição..." rows={3} className="flex w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" data-testid="input-page-bio" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Cor do nome</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input type="color" value={pageSettings.cor_fonte_nome || '#333333'} onChange={e => setPageSettings({...pageSettings, cor_fonte_nome: e.target.value})} className="w-8 h-8 rounded cursor-pointer border-0" data-testid="color-page-nome" />
                      <Input value={pageSettings.cor_fonte_nome || ''} onChange={e => setPageSettings({...pageSettings, cor_fonte_nome: e.target.value})} className="text-xs" />
                    </div>
                  </div>
                  <div>
                    <Label>Cor da bio</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input type="color" value={pageSettings.cor_fonte_bio || '#333333'} onChange={e => setPageSettings({...pageSettings, cor_fonte_bio: e.target.value})} className="w-8 h-8 rounded cursor-pointer border-0" data-testid="color-page-bio" />
                      <Input value={pageSettings.cor_fonte_bio || ''} onChange={e => setPageSettings({...pageSettings, cor_fonte_bio: e.target.value})} className="text-xs" />
                    </div>
                  </div>
                </div>
                <div>
                  <Label>Fonte</Label>
                  <Select value={pageSettings.fonte || 'Inter'} onValueChange={v => setPageSettings({...pageSettings, fonte: v})}>
                    <SelectTrigger data-testid="select-page-font"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FONT_OPTIONS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Icon Group */}
              <div className="border border-slate-200 rounded-xl p-4 space-y-3">
                <Label className="text-sm font-semibold text-slate-700 block">Ícone</Label>
                <div><IconPicker value={pageSettings.icone_pagina || 'Home'} onChange={v => setPageSettings({...pageSettings, icone_pagina: v})} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Cor do ícone</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input type="color" value={pageSettings.cor_icone || '#ffffff'} onChange={e => setPageSettings({...pageSettings, cor_icone: e.target.value})} className="w-8 h-8 rounded cursor-pointer border-0" data-testid="color-icon" />
                      <Input value={pageSettings.cor_icone || ''} onChange={e => setPageSettings({...pageSettings, cor_icone: e.target.value})} className="text-xs" />
                    </div>
                  </div>
                  <div>
                    <Label>Fundo do ícone</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input type="color" value={pageSettings.cor_fundo_icone || '#2663eb'} onChange={e => setPageSettings({...pageSettings, cor_fundo_icone: e.target.value})} className="w-8 h-8 rounded cursor-pointer border-0" data-testid="color-icon-bg" />
                      <Input value={pageSettings.cor_fundo_icone || ''} onChange={e => setPageSettings({...pageSettings, cor_fundo_icone: e.target.value})} className="text-xs" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Background Group */}
              <div className="border border-slate-200 rounded-xl p-4 space-y-3">
                <Label className="text-sm font-semibold text-slate-700 block">Fundo da Página</Label>

                {/* Background Type Selector */}
                <div className="flex gap-2">
                  {[
                    { value: 'solid', label: 'Cor Sólida' },
                    { value: 'gradient', label: 'Gradiente' },
                    { value: 'preset', label: 'Temas' },
                  ].map(t => (
                    <button key={t.value} onClick={() => setPageSettings({...pageSettings, bg_type: t.value})}
                      className={`flex-1 text-xs py-2 px-3 rounded-lg border transition-all font-medium ${(pageSettings.bg_type || 'gradient') === t.value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                      data-testid={`bg-type-${t.value}`}>
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* Solid Color */}
                {(pageSettings.bg_type || 'gradient') === 'solid' && (
                  <div>
                    <Label>Cor de fundo</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input type="color" value={pageSettings.cor_fundo || '#f8fafc'} onChange={e => setPageSettings({...pageSettings, cor_fundo: e.target.value})} className="w-8 h-8 rounded cursor-pointer border-0" data-testid="color-page-bg" />
                      <Input value={pageSettings.cor_fundo || ''} onChange={e => setPageSettings({...pageSettings, cor_fundo: e.target.value})} className="text-xs" />
                    </div>
                  </div>
                )}

                {/* Gradient */}
                {(pageSettings.bg_type || 'gradient') === 'gradient' && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Cor 1</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <input type="color" value={pageSettings.cor_fundo || '#f8fafc'} onChange={e => setPageSettings({...pageSettings, cor_fundo: e.target.value})} className="w-8 h-8 rounded cursor-pointer border-0" data-testid="gradient-color-1" />
                          <Input value={pageSettings.cor_fundo || ''} onChange={e => setPageSettings({...pageSettings, cor_fundo: e.target.value})} className="text-xs" />
                        </div>
                      </div>
                      <div>
                        <Label>Cor 2</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <input type="color" value={pageSettings.cor_fundo_2 || '#e5f6ff'} onChange={e => setPageSettings({...pageSettings, cor_fundo_2: e.target.value})} className="w-8 h-8 rounded cursor-pointer border-0" data-testid="gradient-color-2" />
                          <Input value={pageSettings.cor_fundo_2 || ''} onChange={e => setPageSettings({...pageSettings, cor_fundo_2: e.target.value})} className="text-xs" />
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label>Direção</Label>
                      <Select value={pageSettings.bg_gradient_direction || 'to bottom right'} onValueChange={v => setPageSettings({...pageSettings, bg_gradient_direction: v})}>
                        <SelectTrigger data-testid="gradient-direction"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {GRADIENT_DIRECTIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Gradient Preview */}
                    <div className="rounded-lg h-16 border border-slate-200 overflow-hidden" data-testid="gradient-preview"
                      style={{ background: `linear-gradient(${pageSettings.bg_gradient_direction || 'to bottom right'}, ${pageSettings.cor_fundo || '#f8fafc'}, ${pageSettings.cor_fundo_2 || '#e5f6ff'})` }} />
                  </div>
                )}

                {/* Preset Themes */}
                {(pageSettings.bg_type) === 'preset' && (
                  <div style={{ marginTop: '16px' }}>
                    <Label className="mb-2 block">Escolha um tema</Label>
                    <div className="grid grid-cols-3 gap-2" data-testid="preset-grid">
                      {BG_PRESETS.map(p => (
                        <button key={p.id} onClick={() => {
                          const isDark = p.id === 'twilight' || p.id === 'midnight';
                          setPageSettings({
                            ...pageSettings,
                            bg_preset: p.id,
                            ...(isDark ? { cor_fonte_nome: '#ffffff', cor_fonte_bio: '#cbd5e1' } : {}),
                          });
                        }}
                          className={`rounded-lg h-16 border-2 transition-all relative overflow-hidden ${pageSettings.bg_preset === p.id ? 'border-blue-600 ring-2 ring-blue-200' : 'border-slate-200 hover:border-slate-300'}`}
                          style={{ background: p.css }}
                          data-testid={`preset-${p.id}`}>
                          <span className={`absolute bottom-1 left-0 right-0 text-center text-[10px] font-medium ${p.id === 'twilight' || p.id === 'midnight' ? 'text-white/80' : 'text-slate-500'}`}>
                            {p.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Button onClick={savePageSettings} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="btn-save-page-settings">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                Salvar Configurações
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir Link</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">Tem certeza que deseja excluir <strong>{deleteConfirm?.titulo}</strong>?</p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} data-testid="cancel-delete-btn">Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDelete(deleteConfirm?.link_id)} data-testid="confirm-delete-btn">Excluir</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={resetStyleConfirm} onOpenChange={setResetStyleConfirm}>
        <DialogContent className="max-w-md" data-testid="reset-link-style-dialog">
          <DialogHeader>
            <DialogTitle>Resetar configurações padrões?</DialogTitle>
            <DialogDescription>
              A aparência da página e dos botões voltará ao padrão. Links, URLs, títulos, descrições e ordem serão mantidos.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setResetStyleConfirm(false)} data-testid="reset-link-style-cancel">Cancelar</Button>
            <Button onClick={resetDefaultStyle} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="reset-link-style-confirm">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1.5" />}Resetar aparência
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Info Modal — Links */}
      <Dialog open={showLinksInfo} onOpenChange={setShowLinksInfo}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="text-base">Como funciona o módulo de Links?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-slate-600">
            <div>
              <p className="font-semibold text-slate-800 mb-1">O que são os Links?</p>
              <p>Os links compõem a página pública da sua igreja (Linktree). Cada link pode apontar para redes sociais, formulários ou páginas personalizadas acessíveis por qualquer pessoa.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-800 mb-1">Links de Sistema</p>
              <p>Alguns links são gerados automaticamente pelo sistema, como Cadastro, Atualizar Dados, Perguntas e Solicitar Visita. Eles são identificados com o badge Sistema e não podem ser excluídos, apenas ativados/desativados.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-800 mb-1">Links Personalizados</p>
              <p>Você pode adicionar links livres (Instagram, YouTube, etc.) com ícone, cor e descrição personalizáveis. Esses links podem ser reordenados, duplicados e excluídos livremente.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-800 mb-1">Configurações da Página</p>
              <p>Use o ícone de engrenagem para personalizar o visual da página pública: fonte, cor de fundo, imagem de capa, etc.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
